import React, { useState } from 'react';
import { Download, Upload, FileText, Share2, Copy, Check } from 'lucide-react';
import { useNotifications } from './NotificationSystem';
import apiService from '../services/api';

const ExportImportModal = ({ flow, onImport, onClose }) => {
  const { showSuccess, showError, showWarning } = useNotifications();
  const [activeTab, setActiveTab] = useState('export');
  const [exportData, setExportData] = useState('');
  const [importData, setImportData] = useState('');
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const metadata = {
        name: flowName || 'Untitled Flow',
        description: flowDescription,
        author: 'AI Flow User',
      };

      const exportedFlow = await apiService.exportFlow(flow, metadata);
      const jsonString = JSON.stringify(exportedFlow, null, 2);
      setExportData(jsonString);
      showSuccess('Flow exported successfully!', 'Export Complete');
    } catch (error) {
      console.error('Export failed:', error);
      showError('Failed to export flow. Please try again.', 'Export Error');
    } finally {
      setLoading(false);
    }
  };

  const downloadFlow = () => {
    if (!exportData) return;

    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${flowName || 'ai-flow'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    if (!exportData) return;

    try {
      await navigator.clipboard.writeText(exportData);
      setCopied(true);
      showSuccess('Copied to clipboard!', 'Copy Success');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showError('Failed to copy to clipboard. Please try again.', 'Copy Error');
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      showWarning('Please paste flow data to import', 'Import Warning');
      return;
    }

    setLoading(true);
    try {
      const parsedData = JSON.parse(importData);
      const importedFlow = await apiService.importFlow(parsedData);
      onImport(importedFlow);
      showSuccess('Flow imported successfully!', 'Import Complete');
      onClose();
    } catch (error) {
      console.error('Import failed:', error);
      showError('Failed to import flow. Please check the data format and try again.', 'Import Error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setImportData(e.target.result);
    };
    reader.readAsText(file);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Export / Import Flow
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('export')}
            className={`flex-1 px-4 py-2 text-sm font-medium ${
              activeTab === 'export'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Download size={16} className="inline mr-2" />
            Export Flow
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`flex-1 px-4 py-2 text-sm font-medium ${
              activeTab === 'import'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Upload size={16} className="inline mr-2" />
            Import Flow
          </button>
        </div>

        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {activeTab === 'export' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Flow Name
                  </label>
                  <input
                    type="text"
                    value={flowName}
                    onChange={(e) => setFlowName(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter flow name..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={flowDescription}
                    onChange={(e) => setFlowDescription(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brief description..."
                  />
                </div>
              </div>

              <button
                onClick={handleExport}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
              >
                <FileText size={16} />
                {loading ? 'Generating...' : 'Generate Export Data'}
              </button>

              {exportData && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700">
                      Export Data
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={copyToClipboard}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        {copied ? <Check size={12} /> : <Copy size={12} />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        onClick={downloadFlow}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      >
                        <Download size={12} />
                        Download
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={exportData}
                    readOnly
                    className="w-full h-40 p-3 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono resize-none"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Import Method
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                      <Upload size={16} />
                      Upload File
                    </div>
                  </label>
                  <span className="text-gray-500 self-center">or paste data below</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flow Data (JSON)
                </label>
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  className="w-full h-40 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono resize-none"
                  placeholder="Paste your exported flow data here..."
                />
              </div>

              <button
                onClick={handleImport}
                disabled={loading || !importData.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Upload size={16} />
                {loading ? 'Importing...' : 'Import Flow'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportImportModal;
